// src/screens/Profil.jsx
// 🎨 Profil créateur — design aligné sur l'app Flutter (Profil_v3)
// Header dégradé violet, avatar cerclé, onglets Compte / Société / Sécurité.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEye, FaEyeSlash, FaInstagram, FaFacebookF, FaLinkedinIn, FaYoutube,
  FaCamera, FaImage, FaSignOutAlt, FaAt, FaUser, FaBuilding, FaLock,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loader2 } from "lucide-react";
import {
  createurGetMyProfil,
  createurUpdateProfile,
  createurUpdateImage,
  createurUpdatePassword,
  createurUpdateBackground,
} from "../services/CreateurService";

const VIOLET = "#49179A";
const GRADIENT = "linear-gradient(135deg, #8A2BE2 0%, #49179A 100%)";
const TYPES_IMAGE = ["image/jpeg", "image/jpg", "image/png"];

const Chargement = () => (
  <div className="w-full h-screen flex flex-col items-center justify-center gap-4" data-testid="profil-chargement">
    <Loader2 className="animate-spin w-12 h-12 text-color1" />
    <p className="text-gray-500 text-lg">Chargement...</p>
  </div>
);

// ── Champ texte stylé ──────────────────────────────────────
const Champ = ({ label, icon, className = "", ...props }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    {label && <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>}
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{icon}</span>}
      <input
        {...props}
        className={`w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 text-sm text-gray-800
          focus:outline-none focus:border-[#49179A] focus:ring-2 focus:ring-[#49179A]/15 transition
          ${icon ? "pl-10 pr-3" : "px-3"}`}
      />
    </div>
  </div>
);

// ── Champ mot de passe avec œil ────────────────────────────
const ChampMdp = ({ label, value, onChange, visible, onToggle, testid }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 pr-10 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#49179A] focus:ring-2 focus:ring-[#49179A]/15 transition"
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
        {visible ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  </div>
);

// ── Bouton rond translucide (comme les icônes du header Flutter) ──
const BoutonRond = ({ title, onClick, children, danger, as = "button", testid }) => {
  const Comp = as;
  return (
    <Comp
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`w-9 h-9 flex items-center justify-center rounded-full border cursor-pointer transition text-white text-sm
        ${danger
          ? "bg-red-500/20 border-red-300/30 hover:bg-red-500/40"
          : "bg-white/10 border-white/20 hover:bg-white/20"}`}
    >
      {children}
    </Comp>
  );
};

const ONGLETS = [
  { id: "compte", label: "Compte", icon: <FaUser /> },
  { id: "societe", label: "Société", icon: <FaBuilding /> },
  { id: "securite", label: "Sécurité", icon: <FaLock /> },
];

const ProfilCreateur = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [onglet, setOnglet] = useState("compte");
  const [savingProfil, setSavingProfil] = useState(false);
  const [savingMdp, setSavingMdp] = useState(false);
  const [uploading, setUploading] = useState(null); // "photo" | "fond" | null

  const [form, setForm] = useState({
    telephone: "", bio: "",
    instagram_url: "", facebook_url: "", linkedin_url: "", youtube_url: "",
    siret: "", raison_sociale: "", rue_siege: "", code_postal_siege: "", ville_siege: "", dirigeant_siege: "",
  });

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [voir, setVoir] = useState({ old: false, new: false, confirm: false });

  useEffect(() => {
    if (!token) { navigate("/login"); return; }

    const fetchProfil = async () => {
      try {
        const profil = await createurGetMyProfil(token);
        if (!profil) { toast.error("Impossible de récupérer le profil"); return; }
        setUser(profil);
        setForm({
          telephone: profil.telephone || "",
          bio: profil.bio || "",
          instagram_url: profil.instagram_url || "",
          facebook_url: profil.facebook_url || "",
          linkedin_url: profil.linkedin_url || "",
          youtube_url: profil.youtube_url || "",
          siret: profil.siret || "",
          raison_sociale: profil.raison_sociale || "",
          rue_siege: profil.rue_siege || "",
          code_postal_siege: profil.code_postal_siege || "",
          ville_siege: profil.ville_siege || "",
          dirigeant_siege: profil.dirigeant_siege || "",
        });
      } catch {
        toast.error("Erreur lors du chargement du profil");
      }
    };
    fetchProfil();
  }, [token, navigate]);

  if (!user) return <Chargement />;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfil(true);
    try {
      const updated = await createurUpdateProfile(form, token);
      setUser(updated);
      toast.success("Profil mis à jour ✅");
    } catch (err) {
      toast.error(err.message || "Erreur lors de la mise à jour du profil");
    } finally {
      setSavingProfil(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword) { toast.error("Remplis tous les champs"); return; }
    if (newPassword !== confirmPassword) { toast.error("Les mots de passe ne correspondent pas ❌"); return; }

    setSavingMdp(true);
    try {
      const res = await createurUpdatePassword(oldPassword, newPassword, confirmPassword, token);
      toast.success(res.message || "Mot de passe changé ✅");
      setOldPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(err.message || "Erreur lors du changement de mot de passe ❌");
    } finally {
      setSavingMdp(false);
    }
  };

  // Upload générique (photo de profil ou fond)
  const uploader = async (e, type) => {
    const file = e.target.files[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    if (!TYPES_IMAGE.includes(file.type)) {
      toast.error("Seules les images JPG, JPEG et PNG sont autorisées ❌");
      return;
    }
    setUploading(type);
    try {
      if (type === "photo") await createurUpdateImage(file, token);
      else await createurUpdateBackground(file, token);
      window.location.reload();
    } catch (err) {
      toast.error(err.message || "Erreur lors du changement d'image");
      setUploading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    navigate("/devenir-partenaire");
  };

  const initiales = user.nomComplet?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  const reseaux = [
    { url: user.instagram_url, icon: <FaInstagram />, nom: "Instagram" },
    { url: user.facebook_url, icon: <FaFacebookF />, nom: "Facebook" },
    { url: user.linkedin_url, icon: <FaLinkedinIn />, nom: "LinkedIn" },
    { url: user.youtube_url, icon: <FaYoutube />, nom: "YouTube" },
  ].filter((r) => r.url);

  // Photo de fond valide ? Sinon on garde le dégradé violet seul.
  const fond = (user.photo_background || "").trim();
  const aFond =
    /^https?:\/\//i.test(fond) &&
    !/placeholder/i.test(fond) &&
    fond !== "empty";
  return (
    <div className="max-w-4xl mx-auto pb-10 px-1 lg:px-6 lg:pt-6" data-testid="page-profil-createur">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ══════════════════ HEADER ══════════════════ */}
      {/* ══════════════════ HEADER ══════════════════ */}
      <div
        className="relative isolate rounded-b-[28px] lg:rounded-[28px] overflow-hidden "
        style={{ backgroundColor: "#49179A" }}
      >
        {/* Calque de fond : la photo seule, sinon le dégradé violet */}
        <div
          className="absolute inset-0"
          style={{
            zIndex: 0,
            backgroundImage: aFond ? `url("${fond}")` : GRADIENT,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Voile sombre léger — garde le texte blanc lisible sur la photo */}
        {aFond && <div className="absolute inset-0 z-[1] bg-black/35" />}

        <div className="relative z-10 px-5 lg:px-8 pt-5 pb-6">
          {/* Ligne : avatar + actions */}
          <div className="flex items-start justify-between gap-4">
            {/* Avatar cerclé */}
            <label className="relative cursor-pointer group shrink-0" title="Changer la photo">
              <div
                className="w-24 h-24 lg:w-28 lg:h-28 rounded-full p-[3px]"
                style={{ background: "#8A2BE2", boxShadow: "0 0 18px rgba(138,43,226,0.6)" }}
              >
                <div className="w-full h-full rounded-full overflow-hidden bg-[#49179A] flex items-center justify-center text-white text-2xl font-bold">
                  {user.photo_profil
                    ? <img src={user.photo_profil} alt={user.nomComplet} className="w-full h-full object-cover" />
                    : <span>{initiales}</span>}
                </div>
              </div>
              <div className="absolute inset-[3px] rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                {uploading === "photo"
                  ? <Loader2 className="animate-spin w-6 h-6 text-white" />
                  : <FaCamera className="text-white" />}
              </div>
              <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-[#49179A]" style={{ backgroundColor: "#8A2BE2" }}>
                <FaCamera className="text-white text-[11px]" />
              </span>
              <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="input-photo-profil"
                onChange={(e) => uploader(e, "photo")} disabled={!!uploading} />
            </label>

            {/* Actions */}
            <div className="flex gap-2">
              <BoutonRond as="label" title="Modifier le fond" testid="btn-modifier-fond">
                {uploading === "fond" ? <Loader2 className="animate-spin w-4 h-4" /> : <FaImage />}
                <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="input-fond"
                  onChange={(e) => uploader(e, "fond")} disabled={!!uploading} />
              </BoutonRond>
              <BoutonRond title="Déconnexion" onClick={handleLogout} danger testid="btn-deconnexion">
                <FaSignOutAlt />
              </BoutonRond>
            </div>
          </div>

          {/* Nom + bio + email */}
          <div className="mt-4">
            <h1 className="text-white font-bold text-2xl lg:text-3xl truncate">{user.nomComplet}</h1>

            {user.bio?.trim() ? (
              <p className="text-[#E1DFDF] text-sm mt-2 leading-snug line-clamp-2">{user.bio}</p>
            ) : (
              <button
                onClick={() => setOnglet("compte")}
                className="mt-2 text-sm font-medium text-[#D9B8FF] hover:text-white transition flex items-center gap-1.5"
              >
                ⊕ Ajouter une bio
              </button>
            )}

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4">
              <span className="flex items-center gap-1.5 text-white/50 text-xs">
                <FaAt /> {user.email}
              </span>
              {reseaux.length > 0 && <span className="w-1 h-1 rounded-full bg-white/25" />}
              <div className="flex gap-2">
                {reseaux.map((r) => (
                  <a
                    key={r.nom} href={r.url} target="_blank" rel="noreferrer" title={r.nom}
                    className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80 text-xs hover:bg-white/25 transition"
                  >
                    {r.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════ ONGLETS ══════════════════ */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl mt-6 mx-2 lg:mx-0" data-testid="onglets-profil">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            onClick={() => setOnglet(o.id)}
            data-testid={`onglet-${o.id}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition
              ${onglet === o.id ? "bg-white shadow text-[#49179A]" : "text-gray-500 hover:text-gray-800"}`}
          >
            <span className="text-xs">{o.icon}</span> {o.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ CONTENU ══════════════════ */}
      <div className="mt-4 mx-2 lg:mx-0 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 lg:p-7">

        {/* ── COMPTE ── */}
        {onglet === "compte" && (
          <form onSubmit={handleUpdateProfile} className="flex flex-col gap-5" data-testid="form-compte">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Bio</label>
              <textarea
                name="bio" value={form.bio} onChange={handleChange} rows={4} maxLength={500}
                placeholder="Présente-toi en quelques mots…"
                data-testid="input-bio"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:border-[#49179A] focus:ring-2 focus:ring-[#49179A]/15 transition"
              />
              <span className="text-[11px] text-gray-400 text-right">{form.bio.length}/500</span>
            </div>

            <h3 className="text-sm font-bold text-gray-800">Réseaux sociaux</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Champ name="instagram_url" value={form.instagram_url} onChange={handleChange} placeholder="https://instagram.com/…" icon={<FaInstagram />} data-testid="input-instagram" />
              <Champ name="facebook_url" value={form.facebook_url} onChange={handleChange} placeholder="https://facebook.com/…" icon={<FaFacebookF />} data-testid="input-facebook" />
              <Champ name="linkedin_url" value={form.linkedin_url} onChange={handleChange} placeholder="https://linkedin.com/…" icon={<FaLinkedinIn />} data-testid="input-linkedin" />
              <Champ name="youtube_url" value={form.youtube_url} onChange={handleChange} placeholder="https://youtube.com/…" icon={<FaYoutube />} data-testid="input-youtube" />
            </div>

            <BoutonSauver loading={savingProfil} testid="btn-sauver-compte" />
          </form>
        )}

        {/* ── SOCIÉTÉ ── */}
        {onglet === "societe" && (
          <form onSubmit={handleUpdateProfile} className="flex flex-col gap-5" data-testid="form-societe">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Champ label="Raison sociale" name="raison_sociale" value={form.raison_sociale} onChange={handleChange} placeholder="Ex : Mon Entreprise SARL" data-testid="input-raison-sociale" />
              <Champ label="SIRET" name="siret" value={form.siret} onChange={handleChange} placeholder="Ex : 123 456 789 00012" data-testid="input-siret" />
              <Champ label="Téléphone" name="telephone" value={form.telephone} onChange={handleChange} placeholder="Ex : 0612345678" data-testid="input-telephone" />
              <Champ label="Dirigeant" name="dirigeant_siege" value={form.dirigeant_siege} onChange={handleChange} placeholder="Ex : Jean Dupont" data-testid="input-dirigeant" />
              <Champ label="Rue du siège" name="rue_siege" value={form.rue_siege} onChange={handleChange} placeholder="Ex : 12 rue de Paris" className="lg:col-span-2" data-testid="input-rue" />
              <Champ label="Code postal" name="code_postal_siege" value={form.code_postal_siege} onChange={handleChange} placeholder="Ex : 75001" data-testid="input-cp" />
              <Champ label="Ville" name="ville_siege" value={form.ville_siege} onChange={handleChange} placeholder="Ex : Paris" data-testid="input-ville" />
            </div>

            <BoutonSauver loading={savingProfil} testid="btn-sauver-societe" />
          </form>
        )}

        {/* ── SÉCURITÉ ── */}
        {onglet === "securite" && (
          <form onSubmit={handleUpdatePassword} className="flex flex-col gap-4 max-w-md" data-testid="form-securite">
            <ChampMdp label="Ancien mot de passe" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
              visible={voir.old} onToggle={() => setVoir({ ...voir, old: !voir.old })} testid="input-ancien-mdp" />
            <ChampMdp label="Nouveau mot de passe" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              visible={voir.new} onToggle={() => setVoir({ ...voir, new: !voir.new })} testid="input-nouveau-mdp" />
            <ChampMdp label="Confirmer le mot de passe" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              visible={voir.confirm} onToggle={() => setVoir({ ...voir, confirm: !voir.confirm })} testid="input-confirmer-mdp" />

            <button
              type="submit" disabled={savingMdp} data-testid="btn-modifier-mdp"
              className="mt-2 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition hover:brightness-110 flex items-center justify-center gap-2"
              style={{ backgroundColor: VIOLET }}
            >
              {savingMdp && <Loader2 className="animate-spin w-4 h-4" />}
              {savingMdp ? "Modification..." : "Modifier le mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const BoutonSauver = ({ loading, testid }) => (
  <button
    type="submit" disabled={loading} data-testid={testid}
    className="self-start px-8 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition hover:brightness-110 flex items-center gap-2"
    style={{ backgroundColor: VIOLET }}
  >
    {loading && <Loader2 className="animate-spin w-4 h-4" />}
    {loading ? "Sauvegarde..." : "Sauvegarder"}
  </button>
);

export default ProfilCreateur;